import React, {useState, useEffect, useRef, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {
  appSpecializations,
  allSpecialists,
  allSpecialistsNextPage,
  patientDashboardLoadingStatus,
  patientDashboardSuccessState,
  patientDashboardFailureState,
  dashboardSearchTerm,
  hospitalBookingModalState,
  recentCasesSelector,
} from '@/services/redux/selectors';
import {
  getAllSpecialistsRequest,
  resetPatientDashboardMessage,
  resetSpecialistList,
  specialistBookingRequest,
  setDashboardSearchTerm,
  setHospitalBookingModalState,
  updateHospitalBookingModalState,
  clearHospitalBookingModalState,
  addFavouriteDoctorRequest,
  removeFavouriteDoctorRequest,
  getRecentCasesRequest,
} from '@/services/redux/slices';

import {Doctor, GetSpecialistListRequestPayload} from '@/interface';
import {Button, Text, TextInput} from '@/ui-kits';
import {SpecialistCard} from '@/components';
import {HospitalBookingModal} from '@/components/specialist-screen/HospitalBookingModal';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {useToast, useInfiniteScroll} from '@/hooks';
import { cn } from '@/utils/common_functions';
import {Routes} from '@/navigation/Routes';

export const FavoritesScreen: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const navigate = useNavigate();
  const location = useLocation();
  const {showToast} = useToast();
  const dispatch = useDispatch();

  // ==========================
  // selectors
  // ==========================
  const specialization = useSelector(appSpecializations);
  const specialistList = useSelector(allSpecialists);
  const nextPage = useSelector(allSpecialistsNextPage);
  const searchTerm = useSelector(dashboardSearchTerm);
  const patientDashboardSuccess = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const patientDashboardFailure = useSelector(patientDashboardFailureState) as ErrorCodes;
  const isLoading = useSelector(patientDashboardLoadingStatus);
  const savedModalState = useSelector(hospitalBookingModalState);
  const recentCases = useSelector(recentCasesSelector);

  // Filter specialists to show only favorites
  const favoriteSpecialists = useMemo(() => {
    return specialistList.filter(specialist => specialist.is_favourite === true);
  }, [specialistList]);

  // ==========================
  // states
  // ==========================
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [showAllCategories, setShowAllCategories] = useState<boolean>(false);

  // Search state for mobile
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Refs for tracking mount
  const isInitialMount = useRef<boolean>(true);

  // Hospital booking modal states
  const [isHospitalBookingModalOpen, setIsHospitalBookingModalOpen] = useState<boolean>(false);
  const [selectedHospital, setSelectedHospital] = useState<Doctor | null>(null);

  // Check if any filters are applied
  const hasActiveFilters = Boolean(selectedCategory);
  const displayedCategories = showAllCategories ? specialization : specialization.slice(0, 6);

  // ==========================
  // functions
  // ==========================
  const handleSelectDoctor = (id: number | null) => {
    setSelectedDoctorId(id);
  };

  // ================
  // Booking Handlers
  // ================
  const handleBookNow = (doctor: Doctor) => {
    dispatch(specialistBookingRequest({id: doctor.id}));
  };

  const handleHospitalBooking = (doctor: Doctor) => {
    setSelectedHospital(doctor);
    setIsHospitalBookingModalOpen(true);
    dispatch(
      setHospitalBookingModalState({
        returnPath: Routes.FAVORITES,
        hospitalId: doctor.id,
        hospitalName: doctor.name,
        specializations: doctor.available_specializations || [],
        selectedSpecialization: null,
        isStep2: false,
        dashboardSearch: searchTerm,
      }),
    );
  };

  const handleCloseHospitalModal = () => {
    setIsHospitalBookingModalOpen(false);
    setSelectedHospital(null);
    dispatch(clearHospitalBookingModalState());
  };

  // ================
  // Favorite Toggle Handler
  // ================
  const handleFavoriteToggle = (doctorId: number) => {
    const doctor = specialistList.find(s => s.id === doctorId);
    if (doctor?.is_favourite) {
      dispatch(removeFavouriteDoctorRequest({specialist_id: doctorId}));
    } else {
      dispatch(addFavouriteDoctorRequest({specialist_id: doctorId}));
    }
  };

  // ================
  // Get Last Consulted Date for a Doctor
  // ================
  const getLastConsultedDate = (doctorName: string): string | null => {
    const recentCase = recentCases.find(
      (rc: any) => rc.doctor_name?.toLowerCase() === doctorName?.toLowerCase()
    );
    return recentCase?.close_at || null;
  };

  const handleResetFilters = () => {
    setSelectedCategory(null);
  };

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategory(categoryId);
  };

  /**
   * Build filter params for API requests
   */
  const buildFilterParams = (page: number): GetSpecialistListRequestPayload => {
    const params: GetSpecialistListRequestPayload = {
      page: page,
    };

    if (selectedCategory) {
      params.category = selectedCategory;
    }

    if (searchTerm) {
      params.search = searchTerm;
    }

    return params;
  };

  /**
   * Infinite scroll hook
   */
  const observerTarget = useInfiniteScroll({
    onLoadMore: () => {
      if (nextPage) {
        dispatch(getAllSpecialistsRequest(buildFilterParams(nextPage)));
      }
    },
    hasMore: nextPage !== null,
    isLoading: isLoading,
    rootMargin: '200px',
  });

  // ==========================
  // side effects
  // ==========================
  // Initial load on component mount
  useEffect(() => {
    if (!isInitialMount.current) {
      return;
    }

    // Reset and load specialists on first mount
    dispatch(resetSpecialistList());
    dispatch(getAllSpecialistsRequest(buildFilterParams(1)));
    // Fetch recent cases for showing "Last Consulted" tag
    dispatch(getRecentCasesRequest());
    isInitialMount.current = false;
  }, [dispatch]);

  // Sync local search term with Redux
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Restore hospital booking modal when returning from specialist profile (back button)
  useEffect(() => {
    if (savedModalState && savedModalState.returnPath === Routes.FAVORITES && location.pathname === Routes.FAVORITES) {
      setSelectedHospital({
        id: savedModalState.hospitalId,
        name: savedModalState.hospitalName,
        available_specializations: savedModalState.specializations,
      } as Doctor);
      setIsHospitalBookingModalOpen(true);

      const searchTerm = savedModalState.dashboardSearch || '';
      setLocalSearchTerm(searchTerm);
      dispatch(setDashboardSearchTerm(searchTerm));
    }
  }, [savedModalState, location.pathname]);

  // Handle search change for mobile
  const handleSearchChange = (value: string) => {
    setLocalSearchTerm(value);
    dispatch(setDashboardSearchTerm(value));
  };

  // Reload when filters change
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    dispatch(resetSpecialistList());
    dispatch(getAllSpecialistsRequest(buildFilterParams(1)));
  }, [searchTerm, selectedCategory, dispatch]);

  // Toast messages
  useEffect(() => {
    if (patientDashboardSuccess) {
      if (patientDashboardSuccess !== 'S-10032' && patientDashboardSuccess !== 'S-10096') {
        showToast(getSuccessMessage(patientDashboardSuccess), 'success');
      }
      if (patientDashboardSuccess === 'S-10100') {
        navigate(Routes.DASHBOARD);
      }
      dispatch(resetPatientDashboardMessage());
    }
    if (patientDashboardFailure && patientDashboardFailure !== 'E-10076') {
      showToast(getErrorMessage(patientDashboardFailure), 'failure');
      dispatch(resetPatientDashboardMessage());
    }
  }, [patientDashboardSuccess, patientDashboardFailure]);

  return (
    <div className="min-h-screen w-full bg-[#F5F7FA] mx-auto ">
      {/* header section */}
      <div className="py-2 flex flex-col gap-2">

        {/* Search: responsive layout */}
        <div className="px-4 flex flex-col gap-1">
          {/* Search — full width on small screens, hidden on lg+ */}
          <div className="w-full max-w-[500px] mx-auto lg:hidden flex items-center gap-3">
            <TextInput
              value={localSearchTerm}
              onChangeText={handleSearchChange}
              placeholder="Search by Specialist Name"
              leftIcon="search_blue"
              iconClassName="ml-2"
              wrapperClassName="!bg-white border border-ui_input_border_dark/40 rounded-lg"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Browse by Category Section */}
      {specialization && specialization.length > 0 && (
        <div className="mb-20 bg-white py-5 ">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-FigtreeBold text-ui_blue text-center mb-6 ">Browse Favorites by Category</h2>

          <div className="flex flex-wrap justify-center gap-2 mb-4 px-2">
            {displayedCategories.map(category => {
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm md:text-base transition-all ${
                    isSelected ? 'bg-ui_bg_light_blue font-FigtreeBold text-ui_blue' : 'bg-white font-FigtreeMedium text-ui_blue border border-ui_blue'
                  }`}>
                  <Text variant="small" className={cn(isSelected ? 'gradient-text' : '')}>
                    {category.label}
                  </Text>
                </button>
              );
            })}
          </div>

          {/* See more/less + Reset Filters inline */}
          <div className="flex items-center justify-center gap-4">
            {specialization.length > 6 && (
              <button
                onClick={() => setShowAllCategories(!showAllCategories)}
                className="text-ui_blue/70 text-xs sm:text-sm md:text-base font-PoppinsSemiBold hover:underline">
                {showAllCategories ? 'See less' : 'See more'}
              </button>
            )}
            {hasActiveFilters && (
              <Button onClick={handleResetFilters} className="xl:h-[36px] !w-auto px-5" title="Reset Filters" variant="secondary" />
            )}
          </div>
        </div>
      )}

      {/* Initial Loading State */}
      {isLoading && specialistList.length === 0 && (
        <div className="text-center py-12 px-4">
          <p className="text-[#4a6578] text-base sm:text-lg md:text-xl">Loading favorites...</p>
        </div>
      )}

      {/* Doctors Grid - Only show favorite specialists */}
      {favoriteSpecialists.length > 0 && (
        <>
          <div className="specialist-screen-grid px-4 mb-8 w-full justify-items-center">
            {favoriteSpecialists.map((doctor, index) => (
              <SpecialistCard
                disabled={false}
                key={`${doctor.id}-${index}`}
                doctor={doctor}
                isSelected={doctor.id === selectedDoctorId}
                onSelect={handleSelectDoctor}
                onBookNow={handleBookNow}
                onHospitalBooking={handleHospitalBooking}
                isPopular={(favoriteSpecialists[index] as any)?.popular || false}
                showBookNow={true}
                showFavoriteIcon={true}
                onFavoriteToggle={handleFavoriteToggle}
                lastConsultedDate={getLastConsultedDate(doctor.name)}
              />
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          <div ref={observerTarget} className="h-1" />
        </>
      )}

      {/* No Results - Show different messages based on context */}
      {!isLoading && favoriteSpecialists.length === 0 && (
        <div className="text-center py-12 px-4 max-w-3xl mx-auto">
          {specialistList.length > 0 ? (
            <p className="text-ui_blue text-base sm:text-lg md:text-xl font-bold">No favorite specialists found. Add specialists to your favorites from the Specialists screen.</p>
          ) : !specialization || specialization.length === 0 ? (
            <p className="text-ui_blue text-base sm:text-lg md:text-xl font-bold">Currently no specialist available, please try again in sometime.</p>
          ) : (
            (hasActiveFilters || searchTerm) && (
              <p className="text-ui_blue text-base sm:text-lg md:text-xl font-bold">No favorite Specialists matched your search. Please try again later.</p>
            )
          )}
        </div>
      )}

    </div>
  );
};